import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./ServicesPage.css";
import { useLocalContext } from "../../context/LocalContext";
import {
  FaArrowRight,
  FaCheckCircle,
  FaPhoneAlt,
  FaRegClock,
  FaStar,
} from "react-icons/fa";

const ServicesPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { webinfo = {} } = useLocalContext();

  const [openFaq, setOpenFaq] = useState(-1);

  const serviceData = useMemo(
    () => [
      {
        id: "branding",
        title: "Brand Strategy & Visual Identity",
        description:
          "A premium brand system that instantly builds trust and improves conversion.",
        details:
          "We craft a cohesive brand identity that looks sharp across your website, creatives, and social presence. From positioning and messaging to typography and UI direction — you get a consistent brand people remember and trust.",
        image:
          "https://res.cloudinary.com/dq5rlyl05/image/upload/v1772193423/marketing-promotional-campaign-brand-awareness-building-branded-workshop-workshop-organized-by-brand-useful-marketing-event-concept_335657-115_v55ax8-removebg-preview_nfrkqb_uw4biv.png",
        stats: [
          { label: "Typical timeline", value: "7–14 days", icon: <FaRegClock /> },
          { label: "Best for", value: "Startups & Rebrands", icon: <FaStar /> },
          { label: "Deliverables", value: "Brand Kit + Guide", icon: <FaCheckCircle /> },
        ],
        features: [
          "Brand positioning + messaging framework",
          "Visual identity direction (colors, type, layout rules)",
          "Brand guidelines PDF + editable kit",
          "Social templates (posts, stories, highlight covers)",
          "Consistency rules for future pages & campaigns",
        ],
        testimonials: [
          { name: "Anika Rao", feedback: "Clear direction, premium visuals — our brand finally feels cohesive." },
          { name: "Jay Mehta", feedback: "Website and creatives look consistent now. Trust improved instantly." },
        ],
        faqs: [
          { question: "Can you build a brand from scratch?", answer: "Yes — positioning, voice, identity, and launch-ready assets." },
          { question: "Do we get editable files?", answer: "Yes. You’ll get editable sources + web/print exports." },
        ],
      },

      {
        id: "seo",
        title: "SEO & Content Growth",
        description:
          "Rank for buyer-intent keywords and generate consistent inbound leads.",
        details:
          "We combine technical SEO, content strategy, and authority building to increase visibility and qualified traffic. You’ll get a structured plan, implementation, and reporting focused on conversions — not just rankings.",
        image: "https://res.cloudinary.com/dq5rlyl05/image/upload/v1772193450/7121186-1024x1024_lfwkjx_xeyduy.jpg",
        stats: [
          { label: "First results", value: "4–8 weeks", icon: <FaRegClock /> },
          { label: "Best for", value: "Local + National", icon: <FaStar /> },
          { label: "Focus", value: "Traffic + Leads", icon: <FaCheckCircle /> },
        ],
        features: [
          "Technical audit + fixes (speed, structure, indexing)",
          "Keyword clusters + content map (buyer intent)",
          "On-page SEO (headings, internal links, schema)",
          "Content plan + outlines (optional writing support)",
          "Authority plan (links + topical coverage)",
        ],
        testimonials: [
          { name: "Ritika Deshmukh", feedback: "Organic leads doubled in 90 days. Clear plan and reporting." },
        ],
        faqs: [
          { question: "Do you handle local SEO?", answer: "Yes. GBP/Maps optimization, citations, and location pages." },
          { question: "Do you write content too?", answer: "We can do outlines + optimization or full done-for-you content." },
        ],
      },

      {
        id: "ads",
        title: "Performance Advertising",
        description:
          "Google & Meta ads built for ROI — better leads, better tracking, better scaling.",
        details:
          "We build an ad system: funnel strategy, creative testing, tracking, and optimization. You get structured experiments and clear reporting that focuses on outcomes — leads, ROAS, and revenue.",
        image:
          "https://res.cloudinary.com/dq5rlyl05/image/upload/v1772193511/digital-marketing-strategy-illustration-p6vpak7c24vzqb7u_eht4rk_ark3zf_zsfzlq_bvia4f.png",
        stats: [
          { label: "Setup time", value: "3–5 days", icon: <FaRegClock /> },
          { label: "Best for", value: "Leads / Sales", icon: <FaStar /> },
          { label: "Tracking", value: "Full Funnel", icon: <FaCheckCircle /> },
        ],
        features: [
          "Campaign setup + audience strategy",
          "Creative testing plan + ad copy hooks",
          "Pixel/GA4 events + conversion tracking",
          "Weekly optimization + scaling plan",
          "Reporting dashboard + action items",
        ],
        testimonials: [{ name: "Rohit J.", feedback: "Cleaner targeting + better testing = big improvement in returns." }],
        faqs: [
          { question: "Minimum budget to start?", answer: "₹25k–₹50k/month helps gather enough data to optimize well." },
          { question: "Do you run international campaigns?", answer: "Yes, multi-geo campaigns with localized messaging." },
        ],
      },

      {
        id: "web-dev",
        title: "Web Development & Landing Pages",
        description:
          "High-performance websites that load fast, look premium, and convert better.",
        details:
          "We build conversion-ready websites and landing pages with clean UI, strong UX, and performance at the core. Whether it’s WordPress or a React/Next build — everything is structured for growth and measurable results.",
        image:
          "https://res.cloudinary.com/dq5rlyl05/image/upload/v1772193540/web-development-illustration-download-in-svg-png-gif-file-formats--html-logo-design-ux-illustrations-3181531_e2rbrh_a2qaao.png",
        stats: [
          { label: "Delivery", value: "7–21 days", icon: <FaRegClock /> },
          { label: "Best for", value: "Web + Funnels", icon: <FaStar /> },
          { label: "Outcome", value: "Speed + Leads", icon: <FaCheckCircle /> },
        ],
        features: [
          "WordPress / React / Next.js development",
          "Mobile-first UX + premium UI polish",
          "SEO setup (schema, structure, indexing basics)",
          "Forms, WhatsApp, CRM integrations",
          "Tracking (GA4, Pixel, Hotjar) setup",
        ],
        testimonials: [
          { name: "Kavya Joshi", feedback: "Premium look + super fast load. Lead quality improved too." },
        ],
        faqs: [
          { question: "Can you redesign an existing site?", answer: "Yes — we upgrade UX, speed and SEO while keeping brand consistency." },
          { question: "Do you provide maintenance?", answer: "Yes, monthly support for updates, improvements, and monitoring." },
        ],
      },

      {
        id: "analytics",
        title: "Analytics & Reporting",
        description:
          "Dashboards + tracking so you know what’s working (and what’s wasting money).",
        details:
          "We turn scattered data into clarity. GA4, pixels, events, funnels, and dashboards — so decisions are based on real numbers, not guesses.",
        image:
          "https://res.cloudinary.com/dq5rlyl05/image/upload/v1772193589/data-driven-presentation-illustration-download-in-svg-png-gif-file-formats--business-strategy-delivery-professional-pitch-corporate-communication-employee-pack-illustrations-8491141_htbejp_gcjwva_cvg09r.webp",
        stats: [
          { label: "Setup time", value: "2–5 days", icon: <FaRegClock /> },
          { label: "Best for", value: "Teams & Owners", icon: <FaStar /> },
          { label: "Focus", value: "KPIs + ROI", icon: <FaCheckCircle /> },
        ],
        features: [
          "GA4 + Pixel + event tracking setup",
          "Looker Studio dashboards (real-time KPIs)",
          "Funnel views + drop-off insights",
          "Monthly review + action plan",
          "CRM/offline tracking (if supported)",
        ],
        testimonials: [
          { name: "Imran Ali", feedback: "Now we see exactly which channel works. Clean dashboards." },
        ],
        faqs: [
          { question: "Do we need technical knowledge?", answer: "No — dashboards are built to be understood at a glance." },
          { question: "Can you integrate CRM data?", answer: "Yes — depends on your CRM, we can sync outcomes into reporting." },
        ],
      },

      {
        id: "automation",
        title: "CRM & Marketing Automation",
        description:
          "Follow-ups, workflows, and automation that convert leads without manual chasing.",
        details:
          "We build automation that saves time and increases conversion. Lead capture → routing → WhatsApp/email follow-ups → CRM pipelines — so no lead is missed and response time improves.",
        image:
          "https://res.cloudinary.com/dq5rlyl05/image/upload/v1772193614/gadget-production-automation-illustration-concept-on-white-background-vector-removebg-preview_y7khnk_mcmgav.png",
        stats: [
          { label: "Setup time", value: "5–12 days", icon: <FaRegClock /> },
          { label: "Best for", value: "Sales Teams", icon: <FaStar /> },
          { label: "Outcome", value: "Faster Conversions", icon: <FaCheckCircle /> },
        ],
        features: [
          "Lead capture + scoring + pipeline setup",
          "WhatsApp/email journeys by behavior",
          "CRM setup (HubSpot/Zoho/etc.)",
          "Chatbot + form automations",
          "Reactivation + retention workflows",
        ],
        testimonials: [
          { name: "Sneha Iyer", feedback: "We reclaimed hours weekly. Follow-ups run perfectly now." },
        ],
        faqs: [
          { question: "Can you integrate my existing CRM?", answer: "Yes — we map your pipeline and automate routing & follow-ups." },
          { question: "Do you build custom sequences?", answer: "Yes — inquiry → nurture → close → onboarding flows." },
        ],
      },

      {
        id: "launches",
        title: "Launch Campaigns",
        description:
          "Launch with momentum — funnels, creatives, and demand that converts fast.",
        details:
          "We plan launches that generate attention and turn it into sales/leads. Pre-launch pages, waitlists, sequences, and campaign execution — built as one system.",
        image:
          "https://res.cloudinary.com/dq5rlyl05/image/upload/v1772193648/rocket-launch-teamwork-of-business-people-on-new-vector-43818156-removebg-preview_xhtxs2_lsjoif.png",
        stats: [
          { label: "Planning", value: "5–10 days", icon: <FaRegClock /> },
          { label: "Best for", value: "SaaS / Products", icon: <FaStar /> },
          { label: "Outcome", value: "Traction", icon: <FaCheckCircle /> },
        ],
        features: [
          "Landing page + waitlist funnel",
          "Email sequences + countdowns",
          "Creative + ad strategy",
          "Community/PR support (optional)",
          "Post-launch review + optimization",
        ],
        testimonials: [{ name: "Amit Dube", feedback: "Sold out quickly. The funnel and messaging were on point." }],
        faqs: [
          { question: "What launches do you handle?", answer: "SaaS, products, courses, apps, services, eCommerce." },
          { question: "Do you help after launch?", answer: "Yes — optimization based on performance and feedback." },
        ],
      },

      {
        id: "consulting",
        title: "Growth Consulting",
        description:
          "Founder-level clarity: priorities, roadmap, and execution plan your team can follow.",
        details:
          "If you need direction, we act as your external growth partner. We audit, identify bottlenecks, and give a roadmap with KPIs so your team moves faster without confusion.",
        image:
          "https://res.cloudinary.com/dq5rlyl05/image/upload/v1772193676/marketing-consulting-concept-illustration_114360-9027-removebg-preview_zp5wh3_v7hrme.png",
        stats: [
          { label: "Session", value: "60–90 mins", icon: <FaRegClock /> },
          { label: "Best for", value: "Founders", icon: <FaStar /> },
          { label: "Outcome", value: "Roadmap", icon: <FaCheckCircle /> },
        ],
        features: [
          "Website + funnel audit",
          "Positioning & go-to-market inputs",
          "CRO improvements plan",
          "Milestones + KPI roadmap",
          "Process + SOP recommendations",
        ],
        testimonials: [
          { name: "Meena Shah", feedback: "Like having a CMO for a day — direction became crystal clear." },
        ],
        faqs: [
          { question: "Is this one-time or monthly?", answer: "Both — one session or monthly advisory." },
          { question: "Who is this best for?", answer: "Founders and teams who want faster growth with clarity." },
        ],
      },
    ],
    []
  );

  const service = serviceData.find((s) => s.id === serviceId);

  if (!service) {
    return (
      <div className="srvP">
        <div className="srvP__wrap">
          <h1>Service Not Found</h1>
          <p>Please go back to Services and select a valid service.</p>
          <button className="srvP__btn" onClick={() => navigate("/services")}>
            Back to Services <FaArrowRight />
          </button>
        </div>
      </div>
    );
  }

  const phoneCall = (webinfo.phonecall || "").trim();
  const brand = (webinfo.name || "our team").trim();

  return (
    <section className="srvP">
      {/* HERO */}
      <div className="srvP__hero">
        <div className="srvP__heroWrap">
          <div className="srvP__heroLeft">
            <div className="srvP__crumbs">
              <span onClick={() => navigate("/services")}>Services</span>
              <span className="dot" />
              <span>{service.title}</span>
            </div>

            <h1>{service.title}</h1>
            <p className="srvP__sub">
              {service.description} <span className="srvP__subEm">Built for measurable outcomes.</span>
            </p>

            <div className="srvP__actions">
              <button className="srvP__btn" onClick={() => navigate("/contact")}>
                Get Free Plan <FaArrowRight />
              </button>

              <button
                className="srvP__btn ghost"
                onClick={() =>
                  (window.location.href = phoneCall ? `tel:${phoneCall}` : "/contact")
                }
              >
                <FaPhoneAlt /> Call {brand}
              </button>
            </div>

            <div className="srvP__stats">
              {service.stats?.map((st, i) => (
                <div key={i} className="stat">
                  <div className="statIco">{st.icon}</div>
                  <div className="statTxt">
                    <span className="k">{st.label}</span>
                    <span className="v">{st.value}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="srvP__trustLine">
              <span>✅ Clear scope</span>
              <span>✅ Weekly updates</span>
              <span>✅ Tracking-first</span>
            </div>
          </div>

          <div className="srvP__heroRight">
            <div className="srvP__imgCard">
              <img src={service.image} alt={service.title} />
            </div>

            <div className="srvP__sideCta">
              <h4>Get a quick recommendation</h4>
              <p>
                Tell us your goal and we’ll suggest the best approach + timeline.
              </p>
              <button className="srvP__btn sm" onClick={() => navigate("/contact")}>
                Book Free Audit <FaArrowRight />
              </button>
              <div className="srvP__note">
                Average reply: <b>under 24 hours</b>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative layer */}
        <div className="srvP__heroGrid" aria-hidden="true" />
        <div className="srvP__heroGlow" aria-hidden="true" />
      </div>

      {/* BODY */}
      <div className="srvP__wrap">
        <div className="srvP__grid">
          <div className="srvP__block">
            <h2>What we deliver</h2>
            <p>{service.details}</p>

            <div className="srvP__points">
              {service.features.map((f, i) => (
                <div key={i} className="pt">
                  <FaCheckCircle />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Process strip */}
          <div className="srvP__process">
            <div className="proc">
              <b>1) Audit</b>
              <span>We review your site, traffic sources, and bottlenecks.</span>
            </div>
            <div className="proc">
              <b>2) Plan</b>
              <span>You get a clear scope + timeline + priorities.</span>
            </div>
            <div className="proc">
              <b>3) Execute</b>
              <span>We ship, track results, and improve iteratively.</span>
            </div>
          </div>

          <div className="srvP__block">
            <h2>Client feedback</h2>
            <div className="srvP__tGrid">
              {service.testimonials.map((t, i) => (
                <div className="tCard" key={i}>
                  <p>“{t.feedback}”</p>
                  <span>- {t.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="srvP__block">
            <h2>FAQs</h2>
            <div className="srvP__faq">
              {service.faqs.map((faq, i) => {
                const active = openFaq === i;
                return (
                  <div
                    key={i}
                    className={`faq ${active ? "open" : ""}`}
                    onClick={() => setOpenFaq(active ? -1 : i)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="q">
                      <h4>{faq.question}</h4>
                      <span className="plus">{active ? "–" : "+"}</span>
                    </div>
                    {active && <p className="a">{faq.answer}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="srvP__bottomCta">
          <div>
            <h3>Ready to improve results?</h3>
            <p>Share your goal — we’ll send a plan with next steps.</p>
          </div>
          <button className="srvP__btn" onClick={() => navigate("/contact")}>
            Talk to Us <FaArrowRight />
          </button>
        </div>

        {/* Guarantee strip */}
        <div className="srvP__guarantee">
          <b>No confusion.</b>
          <span>Clear scope + timeline + measurable KPIs from day one.</span>
        </div>
      </div>
    </section>
  );
};

export default ServicesPage;